import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Building2, ChevronDown, Search, Check, ArrowRightCircle } from 'lucide-react';
import { listEmitentes } from '../api/emitente';
import { useEmitenteAtivo } from '../context/EmitenteAtivoContext';
import { formatCNPJ } from '../utils/formatters';

export default function EmitenteSelector({ compact = false }: { compact?: boolean }) {
  const { data: emitentes = [], isLoading } = useQuery({
    queryKey: ['emitentes'],
    queryFn: listEmitentes
  });
  const { emitenteId, setEmitenteId } = useEmitenteAtivo();
  const [open, setOpen] = useState(false);
  const [busca, setBusca] = useState('');
  const popRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Páginas /empresa/:id/* leem o emitente da URL; sem navegar, a troca não
  // teria efeito visível (o useEffect da página devolveria o id da URL).
  function selecionar(id: number) {
    setEmitenteId(id);
    setOpen(false);
    setBusca('');
    const m = location.pathname.match(/^\/empresa\/\d+(\/.*)?$/);
    if (m) navigate(`/empresa/${id}${m[1] ?? ''}`);
  }

  useEffect(() => {
    if (!isLoading && emitentes.length > 0 && emitenteId == null) {
      setEmitenteId(emitentes[0].id);
    }
    if (emitenteId != null && !isLoading && !emitentes.some((e) => e.id === emitenteId)) {
      setEmitenteId(emitentes[0]?.id ?? null);
    }
  }, [emitentes, isLoading, emitenteId, setEmitenteId]);

  useEffect(() => {
    if (!open) return;
    function onClick(ev: MouseEvent) {
      if (popRef.current && !popRef.current.contains(ev.target as Node)) setOpen(false);
    }
    function onEsc(ev: KeyboardEvent) { if (ev.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const atual = useMemo(() => emitentes.find((e) => e.id === emitenteId) || null, [emitentes, emitenteId]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return emitentes;
    // CNPJ alfanumerico: normalizar ambos para upper antes de comparar.
    const qUpper = q.toUpperCase();
    return emitentes.filter((e) => {
      const nome = (e.nome_fantasia || e.razao_social || '').toUpperCase();
      const cnpj = (e.cnpj || '').toUpperCase();
      return nome.includes(qUpper) || cnpj.includes(qUpper);
    });
  }, [emitentes, busca]);

  if (isLoading) return <span className="text-xs text-neutral-500">Carregando...</span>;
  if (emitentes.length === 0) {
    return (
      <Link to="/emitentes/novo" className="text-sm text-emerald-400 hover:underline">
        Cadastrar empresa
      </Link>
    );
  }

  const nomeAtual = atual ? atual.nome_fantasia || atual.razao_social : 'Selecionar empresa';
  const ambiente = atual?.ambiente === 'producao' ? 'producao' : 'homologacao';

  return (
    <div className={`relative ${compact ? '' : 'w-full'}`} ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="group flex items-center gap-2.5 w-full rounded-xl border border-slate-200 bg-white/80 hover:bg-slate-50 hover:border-emerald-500/30 px-3 py-2 text-sm text-left transition-colors dark:border-slate-800 dark:bg-slate-950/70 dark:hover:bg-slate-900"
        aria-haspopup="listbox"
        aria-expanded={open}
        title={atual ? `${nomeAtual} · CNPJ ${formatCNPJ(atual.cnpj)}` : 'Selecionar empresa'}
      >
        <div className="h-7 w-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20 grid place-items-center shrink-0">
          <Building2 className="h-3.5 w-3.5 text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold leading-none">Empresa ativa</div>
          <div className="mt-0.5 truncate font-semibold text-slate-950 leading-tight dark:text-white">{nomeAtual}</div>
        </div>
        {atual && ambiente !== 'producao' && (
          <span className="badge-warning shrink-0">HOMOL</span>
        )}
        <ChevronDown className={`h-4 w-4 shrink-0 text-neutral-500 transition-transform dark:text-neutral-400 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          {/* Backdrop fosco para destacar o popup */}
          <div
            className="fixed inset-0 z-40 bg-slate-950/20 backdrop-blur-sm animate-fade-in dark:bg-black/70"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute left-0 top-full mt-2 w-[min(32rem,calc(100vw-1rem))] z-50 rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden animate-slide-up dark:border-slate-800 dark:bg-slate-950">
          <div className="p-2 border-b border-slate-200 dark:border-slate-800">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500 pointer-events-none" />
              <input
                type="search"
                autoFocus
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar por nome ou CNPJ..."
                className="field-input pl-8 py-1.5 text-sm"
              />
            </div>
          </div>
          <ul className="max-h-80 overflow-y-auto py-1">
            {filtrados.length === 0 && (
              <li className="px-3 py-6 text-sm text-center text-neutral-500">Nenhuma empresa encontrada</li>
            )}
            {filtrados.map((e) => {
              const sel = e.id === emitenteId;
              const nome = e.nome_fantasia || e.razao_social;
              const amb = e.ambiente === 'producao' ? 'producao' : 'homologacao';
              return (
                <li key={e.id}>
                  <button
                    type="button"
                    onClick={() => selecionar(e.id)}
                    className={`w-full px-3 py-2.5 flex items-center gap-2 text-left text-sm hover:bg-slate-100 transition dark:hover:bg-slate-900 ${
                      sel ? 'bg-emerald-50 dark:bg-emerald-500/10' : ''
                    }`}
                  >
                    <span className={`shrink-0 w-4 ${sel ? 'text-emerald-400' : 'text-transparent'}`}>
                      <Check className="h-4 w-4" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block truncate font-semibold text-slate-950 dark:text-white">{nome}</span>
                      <span className="mono block text-[11px] text-neutral-600 dark:text-neutral-400">
                        {formatCNPJ(e.cnpj)}{e.uf ? ` · ${e.uf}` : ''}
                      </span>
                    </span>
                    {amb !== 'producao' && (
                      <span className="badge-warning shrink-0">HOMOL</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-slate-200 p-2 flex items-center justify-between gap-2 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/60">
            <span className="text-xs text-neutral-500 tab-nums">
              {emitentes.length} empresa{emitentes.length === 1 ? '' : 's'}
            </span>
            <Link
              to="/emitentes"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300"
            >
              Ver todas <ArrowRightCircle className="h-3 w-3" />
            </Link>
          </div>
          </div>
        </>
      )}
    </div>
  );
}
